import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FileWarning, Plus, ShieldCheck, ShieldOff, Trash2 } from "lucide-react";
import {
  ClaimGuaranteeCode,
  CopropertyClaimsHistoryDto,
  CopropertyDto,
  UpdateCopropertyClaimsHistoryCommand,
} from "api/coproperty";
import { Dropdown, DropdownItem } from "components/forms/Dropdown";
import { InputText } from "components/forms/InputText";
import { InputWithUnit } from "components/forms/fragments/InputWithUnit";
import { Button } from "components/ui/Button";
import { toDayInput } from "core/utils/dates";
import { ChoiceCardOption, ChoiceCards, CompactBooleanRow, parseDecimal } from "./CopropertyFieldControls";
import { CopropertySection } from "./CopropertyStepLayout";

/** Libellés des garanties de la grille (codes du fichier « Variable MRI »). */
export const CLAIM_GUARANTEE_LABELS: Record<ClaimGuaranteeCode, string> = {
  [ClaimGuaranteeCode.Dde]: "Dégât des eaux",
  [ClaimGuaranteeCode.Inc]: "Incendie et risques divers",
  [ClaimGuaranteeCode.Dom]: "Dommages électriques",
  [ClaimGuaranteeCode.Tng]: "Tempête, neige, grêle",
  [ClaimGuaranteeCode.Cnat]: "Catastrophe naturelle",
  [ClaimGuaranteeCode.Ctec]: "Catastrophe technologique",
  [ClaimGuaranteeCode.Rc]: "RC propriétaire d'immeuble",
  [ClaimGuaranteeCode.Choc]: "Choc de véhicule",
  [ClaimGuaranteeCode.Vvan]: "Vol, vandalisme",
  [ClaimGuaranteeCode.Bdg]: "Bris de glace",
  [ClaimGuaranteeCode.Bdm]: "Bris de machine / informatique",
};

const GUARANTEE_ITEMS: DropdownItem<ClaimGuaranteeCode>[] = (
  Object.keys(CLAIM_GUARANTEE_LABELS) as ClaimGuaranteeCode[]
).map((code) => ({ key: code, label: CLAIM_GUARANTEE_LABELS[code] }));

interface ClaimDraft {
  key: string;
  id: string | null;
  guaranteeCode: ClaimGuaranteeCode | null;
  count: string;
  deductible: string;
  totalAmount: string;
}

let draftKeySeed = 0;
const nextDraftKey = () => `sinistre-${++draftKeySeed}`;

const toInput = (value: number | null | undefined) => (value != null ? String(value) : "");

/**
 * Le client généré sérialise les Date du command via toISOString() : minuit UTC
 * garantit que le jour calendaire envoyé est exactement celui de l'input.
 */
const fromDateInput = (value: string): Date | null => (value ? new Date(`${value}T00:00:00Z`) : null);

/** La période déclarée se termine toujours aujourd'hui et couvre par défaut les trois derniers exercices. */
const CLAIMS_PERIOD_YEARS = 3;
const todayInput = () => toDayInput(new Date());
const defaultPeriodStartInput = () => {
  const start = new Date();
  start.setFullYear(start.getFullYear() - CLAIMS_PERIOD_YEARS);
  return toDayInput(start);
};

/** Un sinistre est déclaré dès qu'une ligne porte un nombre strictement positif. */
const hasDeclaredClaims = (drafts: ClaimDraft[]): boolean =>
  drafts.some((draft) => Number(draft.count) > 0);

/**
 * Situation de sinistralité de la copropriété : une seule question à trois réponses, dont
 * les deux engagements du souscripteur se déduisent.
 */
export type ClaimsSituation = "declared" | "none" | "notInsured";

export const CLAIMS_SITUATION_OPTIONS: ChoiceCardOption<ClaimsSituation>[] = [
  {
    value: "declared",
    label: "Oui, des sinistres ont été déclarés",
    description:
      "Vous renseignez les sinistres par garantie. Le relevé d'information de sinistralité sera demandé à la contractualisation dans tous les cas.",
    icon: <FileWarning className="h-5 w-5" />,
  },
  {
    value: "none",
    label: "Non, aucun sinistre",
    description: "Aucun sinistre n'a été déclaré au cours des 3 dernières années.",
    icon: <ShieldCheck className="h-5 w-5" />,
  },
  {
    value: "notInsured",
    label: "Le risque n'est pas assuré, aucun sinistre connu",
    description: "L'immeuble n'est pas assuré aujourd'hui et aucun sinistre n'est connu.",
    icon: <ShieldOff className="h-5 w-5" />,
  },
];

/** Situation telle que les données la portent, ou null tant qu'elle n'est pas renseignée. */
export const readClaimsSituation = (history: CopropertyClaimsHistoryDto | null | undefined): ClaimsSituation | null => {
  if (history?.claims?.some((claim) => (claim.count ?? 0) > 0)) return "declared";
  if (history?.noClaimsLast3Years === true) return "none";
  if (history?.notInsuredNoKnownClaims === true) return "notInsured";
  return null;
};

/** Libellé de la situation pour les récapitulatifs (même vocabulaire partout). */
export const claimsSituationSummary = (history: CopropertyClaimsHistoryDto | null | undefined): string => {
  switch (readClaimsSituation(history)) {
    case "declared": {
      const count = (history?.claims ?? []).reduce((total, claim) => total + (claim.count ?? 0), 0);
      return `${count} sinistre${count > 1 ? "s" : ""} déclaré${count > 1 ? "s" : ""}`;
    }
    case "none":
      return "Aucun sinistre sur les 3 dernières années";
    case "notInsured":
      return "Risque non assuré, aucun sinistre connu";
    default:
      return "Situation non renseignée";
  }
};

/** Grille valide : une garantie choisie par ligne, chacune une seule fois, période dans l'ordre. */
const validateDrafts = (drafts: ClaimDraft[], periodStart: string, periodEnd: string): string | null => {
  if (periodStart && periodEnd && periodStart > periodEnd) {
    return "Le début de la période doit précéder sa fin.";
  }
  if (drafts.some((draft) => draft.guaranteeCode == null)) {
    return "Choisissez la garantie de chaque ligne, ou retirez la ligne.";
  }
  const codes = drafts.map((draft) => draft.guaranteeCode);
  if (new Set(codes).size !== codes.length) {
    return "Une garantie ne peut apparaître qu'une fois.";
  }
  return null;
};

/** Longueur maximale du motif de résiliation, alignée sur la commande du back. */
const TERMINATION_REASON_MAX_LENGTH = 500;

/**
 * Historique d'assurance valide : les deux dates quand l'interruption est confirmée, dans
 * l'ordre, et un motif quand la résiliation l'est.
 */
const validateInsuranceHistory = (
  interruption: boolean | null,
  interruptionStart: string,
  interruptionEnd: string,
  termination: boolean | null,
  terminationReason: string
): string | null => {
  if (interruption === true && (!interruptionStart || !interruptionEnd)) {
    return "Renseignez les dates de début et de fin de l'interruption de garantie.";
  }
  if (interruptionStart && interruptionEnd && interruptionStart > interruptionEnd) {
    return "La date de fin de l'interruption doit suivre sa date de début.";
  }
  if (termination === true && !terminationReason.trim()) {
    return "Renseignez le motif de la résiliation.";
  }
  return null;
};

/** Les trois questions d'historique et la renonciation à recours réciproque sont obligatoires. */
export const hasAnsweredInsuranceHistory = (answers: {
  insuredLast36Months: boolean | null;
  coverageInterruption: boolean | null;
  termination: boolean | null;
  mutualRecourseWaiver: boolean | null;
}): boolean =>
  answers.insuredLast36Months != null &&
  answers.coverageInterruption != null &&
  answers.termination != null &&
  answers.mutualRecourseWaiver != null;

interface CopropertyClaimsFormProps {
  coproperty: CopropertyDto;
  /**
   * Expose au bouton d'étape la commande à enregistrer, ou null si la grille est
   * invalide (le formulaire affiche alors l'erreur).
   */
  registerBuildCommand: (build: () => UpdateCopropertyClaimsHistoryCommand | null) => void;
  /**
   * La situation de sinistralité doit être renseignée : c'est le cas à la contractualisation,
   * où elle conditionne l'envoi du contrat. Le parcours, lui, ne l'exige pas.
   */
  choiceRequired?: boolean;
  /** Suit la saisie : la page du parcours en déduit l'état de son bouton d'étape. */
  onStateChange?: (state: ClaimsFormState) => void;
}

/** Ce que la page hôte a besoin de savoir du formulaire pour piloter sa navigation. */
export interface ClaimsFormState {
  situation: ClaimsSituation | null;
  /** Au moins une ligne de la grille porte un nombre de sinistres strictement positif. */
  hasDeclaredClaims: boolean;
  /** Les trois questions d'historique d'assurance et la renonciation ont une réponse. */
  historyAnswered: boolean;
}

/**
 * Sinistralité déclarative de la copropriété (écran 16 de la spec) : une question à trois
 * réponses et, quand des sinistres sont déclarés, la période couverte — les trois derniers
 * exercices — et, pour chaque garantie touchée, le nombre de sinistres, la franchise et le
 * coût total. Le relevé d'information de sinistralité est demandé à la contractualisation dans
 * tous les cas, avec ou sans sinistre. Suivent l'historique d'assurance des 36 derniers mois et
 * la renonciation à recours réciproque, obligatoires.
 */
export const CopropertyClaimsForm: FC<CopropertyClaimsFormProps> = ({
  coproperty,
  registerBuildCommand,
  choiceRequired = false,
  onStateChange,
}) => {
  const history = coproperty.claimsHistory;

  const [periodStart, setPeriodStart] = useState(toDayInput(history?.periodStart) || defaultPeriodStartInput());
  const periodEnd = todayInput();
  const [drafts, setDrafts] = useState<ClaimDraft[]>(
    () =>
      history?.claims?.map((claim) => ({
        key: nextDraftKey(),
        id: claim.id ?? null,
        guaranteeCode: claim.guaranteeCode ?? null,
        count: toInput(claim.count),
        deductible: toInput(claim.deductible),
        totalAmount: toInput(claim.totalAmount),
      })) ?? []
  );
  const [situation, setSituation] = useState<ClaimsSituation | null>(() => readClaimsSituation(history));
  const [insuredLast36Months, setInsuredLast36Months] = useState<boolean | null>(history?.insuredLast36Months ?? null);
  const [coverageInterruption, setCoverageInterruption] = useState<boolean | null>(
    history?.coverageInterruptionLast36Months ?? null
  );
  const [interruptionStart, setInterruptionStart] = useState(toDayInput(history?.coverageInterruptionStart));
  const [interruptionEnd, setInterruptionEnd] = useState(toDayInput(history?.coverageInterruptionEnd));
  const [termination, setTermination] = useState<boolean | null>(history?.terminationLast36Months ?? null);
  const [terminationReason, setTerminationReason] = useState(history?.terminationReason ?? "");
  const [mutualRecourseWaiver, setMutualRecourseWaiver] = useState<boolean | null>(
    history?.mutualRecourseWaiver ?? null
  );
  const [showErrors, setShowErrors] = useState(false);

  /** La période et la grille ne concernent que la copropriété qui déclare des sinistres. */
  const showClaimsGrid = situation === "declared";

  const declaredClaims = hasDeclaredClaims(drafts);

  const choiceError = situation == null && choiceRequired ? "Indiquez la situation de sinistralité." : null;
  /** Déclarer des sinistres sans en détailler aucun : l'étape ne peut pas être enregistrée ainsi. */
  const missingClaimError =
    situation === "declared" && !declaredClaims ? "Ajoutez au moins un sinistre, ou indiquez qu'il n'y en a pas eu." : null;
  const gridError = useMemo(
    () => (showClaimsGrid ? validateDrafts(drafts, periodStart, periodEnd) : null),
    [showClaimsGrid, drafts, periodStart, periodEnd]
  );
  const historyAnswered = hasAnsweredInsuranceHistory({
    insuredLast36Months,
    coverageInterruption,
    termination,
    mutualRecourseWaiver,
  });
  const historyMissingError = historyAnswered
    ? null
    : "Répondez aux questions sur l'historique d'assurance et sur la renonciation à recours réciproque.";
  const historyError = useMemo(
    () => validateInsuranceHistory(coverageInterruption, interruptionStart, interruptionEnd, termination, terminationReason),
    [coverageInterruption, interruptionStart, interruptionEnd, termination, terminationReason]
  );
  const error = choiceError ?? missingClaimError ?? gridError ?? historyMissingError ?? historyError;

  useEffect(() => {
    onStateChange?.({ situation, hasDeclaredClaims: declaredClaims, historyAnswered });
  }, [onStateChange, situation, declaredClaims, historyAnswered]);

  const updateDraft = (key: string, patch: Partial<ClaimDraft>) =>
    setDrafts((current) => current.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft)));

  const addDraft = () =>
    setDrafts((current) => [
      ...current,
      { key: nextDraftKey(), id: null, guaranteeCode: null, count: "1", deductible: "", totalAmount: "" },
    ]);

  const removeDraft = (key: string) => setDrafts((current) => current.filter((draft) => draft.key !== key));

  const buildCommand = useCallback((): UpdateCopropertyClaimsHistoryCommand | null => {
    if (error) {
      setShowErrors(true);
      return null;
    }
    return {
      periodStart: fromDateInput(periodStart),
      periodEnd: fromDateInput(periodEnd),
      claims: showClaimsGrid
        ? drafts.map((draft) => ({
            id: draft.id,
            guaranteeCode: draft.guaranteeCode!,
            count: draft.count.trim() ? Math.max(0, Math.trunc(Number(draft.count))) : 0,
            deductible: parseDecimal(draft.deductible),
            totalAmount: parseDecimal(draft.totalAmount),
          }))
        : [],
      // Les deux colonnes portent la situation déclarée faute de sinistre.
      noClaimsLast3Years: situation == null || situation === "declared" ? null : situation === "none",
      notInsuredNoKnownClaims: situation == null || situation === "declared" ? null : situation === "notInsured",
      // Réponses garanties non nulles par historyMissingError.
      insuredLast36Months: insuredLast36Months === true,
      coverageInterruptionLast36Months: coverageInterruption === true,
      // Les réponses conditionnelles ne partent que si la question qui les ouvre vaut oui.
      coverageInterruptionStart: coverageInterruption === true ? fromDateInput(interruptionStart) : null,
      coverageInterruptionEnd: coverageInterruption === true ? fromDateInput(interruptionEnd) : null,
      terminationLast36Months: termination === true,
      terminationReason: termination === true ? terminationReason.trim() || null : null,
      mutualRecourseWaiver: mutualRecourseWaiver === true,
    };
  }, [
    error,
    periodStart,
    periodEnd,
    drafts,
    showClaimsGrid,
    situation,
    insuredLast36Months,
    coverageInterruption,
    interruptionStart,
    interruptionEnd,
    termination,
    terminationReason,
    mutualRecourseWaiver,
  ]);

  useEffect(() => {
    registerBuildCommand(buildCommand);
  }, [registerBuildCommand, buildCommand]);

  /** Garanties encore disponibles pour une ligne : celles non prises par les autres lignes. */
  const itemsFor = (draft: ClaimDraft) =>
    GUARANTEE_ITEMS.filter(
      (item) => item.key === draft.guaranteeCode || !drafts.some((other) => other.key !== draft.key && other.guaranteeCode === item.key)
    );

  return (
    <>
      <CopropertySection title="Situation de sinistralité">
        <ChoiceCards
          label="Sinistres sur les 3 dernières années ?"
          options={CLAIMS_SITUATION_OPTIONS}
          value={situation}
          required={choiceRequired}
          onChange={setSituation}
        />
        {showErrors && choiceError && <p className="text-[13px] text-red-600">{choiceError}</p>}
      </CopropertySection>

      {showClaimsGrid && (
        <CopropertySection title="Période déclarée">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <InputText
              label="Début d'exercice"
              name="periodStart"
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
            <InputText label="Fin d'exercice" name="periodEnd" type="date" value={periodEnd} disabled readOnly />
          </div>
          <p className="text-[13px] text-gray-500">
            La période se termine aujourd'hui et couvre par défaut les trois derniers exercices de la copropriété.
          </p>
        </CopropertySection>
      )}

      {showClaimsGrid && (
        <CopropertySection
          title="Garanties touchées"
          titleAside={
            <Button color="secondary" round disabled={drafts.length >= GUARANTEE_ITEMS.length} onClick={addDraft}>
              <Plus size={16} />
              Ajouter un sinistre
            </Button>
          }
        >
          {drafts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 px-5 py-8 text-center">
              <p className="text-sm font-medium text-gray-700">Aucun sinistre déclaré</p>
              <p className="text-[13px] text-gray-500">
                Ajoutez un sinistre si la copropriété en a subi sur la période.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {drafts.map((draft) => (
                <div key={draft.key} className="rounded-2xl border bg-white p-4 shadow">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Dropdown
                      label="Garantie"
                      name={`${draft.key}-guarantee`}
                      placeholder="Sélectionner"
                      items={itemsFor(draft)}
                      selectedItem={GUARANTEE_ITEMS.find((item) => item.key === draft.guaranteeCode) ?? null}
                      onSelect={(item) => updateDraft(draft.key, { guaranteeCode: item.key as ClaimGuaranteeCode })}
                    />
                    <InputText
                      label="Nombre de sinistres"
                      name={`${draft.key}-count`}
                      type="number"
                      min={0}
                      value={draft.count}
                      onChange={(e) => updateDraft(draft.key, { count: e.target.value })}
                    />
                    <InputWithUnit
                      label="Franchise"
                      name={`${draft.key}-deductible`}
                      unit="€"
                      type="number"
                      min={0}
                      value={draft.deductible}
                      onChange={(e) => updateDraft(draft.key, { deductible: e.target.value })}
                    />
                    <InputWithUnit
                      label="Coût total"
                      name={`${draft.key}-total`}
                      unit="€"
                      type="number"
                      min={0}
                      value={draft.totalAmount}
                      onChange={(e) => updateDraft(draft.key, { totalAmount: e.target.value })}
                    />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeDraft(draft.key)}
                      className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                      Retirer cette garantie
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {missingClaimError && <p className="text-[13px] text-red-600">{missingClaimError}</p>}
          {showErrors && gridError && <p className="text-[13px] text-red-600">{gridError}</p>}
        </CopropertySection>
      )}

      <CopropertySection title="Historique d'assurance">
        <div className="flex flex-col gap-3">
          <CompactBooleanRow
            label="Immeuble assuré au cours des 36 derniers mois"
            required
            value={insuredLast36Months}
            onChange={setInsuredLast36Months}
          />
          <CompactBooleanRow
            label="Le contrat a-t-il fait l'objet d'une interruption de garantie au cours des 36 derniers mois ?"
            required
            value={coverageInterruption}
            onChange={setCoverageInterruption}
          />
          {coverageInterruption === true && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <InputText
                label="Date de début"
                name="coverageInterruptionStart"
                type="date"
                required
                value={interruptionStart}
                onChange={(e) => setInterruptionStart(e.target.value)}
              />
              <InputText
                label="Date de fin"
                name="coverageInterruptionEnd"
                type="date"
                required
                value={interruptionEnd}
                onChange={(e) => setInterruptionEnd(e.target.value)}
              />
            </div>
          )}
          <CompactBooleanRow
            label="Le contrat a-t-il fait l'objet d'une résiliation au cours des 36 derniers mois ?"
            required
            value={termination}
            onChange={setTermination}
          />
          {termination === true && (
            <InputText
              label="Motif"
              name="terminationReason"
              required
              maxLength={TERMINATION_REASON_MAX_LENGTH}
              placeholder="Motif de la résiliation"
              value={terminationReason}
              onChange={(e) => setTerminationReason(e.target.value)}
            />
          )}
        </div>
        {showErrors && historyError && <p className="text-[13px] text-red-600">{historyError}</p>}
      </CopropertySection>

      <CopropertySection title="Renonciation à recours réciproque">
        <CompactBooleanRow
          label="Y a-t-il une renonciation à recours réciproque, incluant une renonciation envers l'assureur ?"
          required
          value={mutualRecourseWaiver}
          onChange={setMutualRecourseWaiver}
        />
        {showErrors && historyMissingError && <p className="text-[13px] text-red-600">{historyMissingError}</p>}
      </CopropertySection>
    </>
  );
};
